import { rebuildPosicions } from "../../../utils/loadPositions";
import { getCaixaId, getIdsInPestanya, isFustesLog, normalizePestanya } from "./importLog";

const ORDER_OF_PRIORITY = [
    'pinya',
    'folre',
    'manilles',
    'puntals',
    'tronc',
    'organització'
];

const splitCaixaId = (caixaId) => {
    const raw = String(caixaId || '');
    const separatorIndex = raw.indexOf('_');

    if (separatorIndex < 0) {
        return {
            raw,
            prefix: null,
            base: raw
        };
    }

    return {
        raw,
        prefix: raw.slice(0, separatorIndex),
        base: raw.slice(separatorIndex + 1)
    };
};

const caixesMatch = (caixaIdA, caixaIdB) => {
    const a = splitCaixaId(caixaIdA);
    const b = splitCaixaId(caixaIdB);

    if (!a.raw || !b.raw) return false;
    if (a.raw === b.raw) return true;

    const onlyOneHasPrefix = (a.prefix === null) !== (b.prefix === null);
    return onlyOneHasPrefix && a.base === b.base;
};

const someCaixaMatches = (caixaIds, caixaId) => (
    caixaIds.some(id => caixesMatch(id, caixaId))
);

const caixaMatchesScopedCaixes = (scopedCaixaIds, caixaId, json = {}) => {
    if (!scopedCaixaIds) return true;
    if (scopedCaixaIds.includes(caixaId)) return true;
    if (caixaId in (json || {})) return false;

    return someCaixaMatches(scopedCaixaIds, caixaId);
};

const removeDuplicatesCaixes = (arr) => {
    const seen = {};

    return [...arr]
        .reverse()
        .reduce((acc, item) => {
            const caixaId = getCaixaId(item);

            if (!seen[caixaId]) {
                seen[caixaId] = true;
                acc.push(item);
            }

            return acc;
        }, [])
        .reverse();
};

const getMergedImportedPosicions = (posicionsLogPerPestanya) => (
    Object.values(posicionsLogPerPestanya || {})
        .filter(entry => entry)
        .map(entry => {
            return ORDER_OF_PRIORITY
                .filter(key => entry?.[key]?.posicions)
                .map(key => removeDuplicatesCaixes(entry[key].posicions)
                    .map(log => ({ log, eventId: entry[key].event })))
                .reduce((acc, posicions) => [...acc, ...posicions], []);
        })
        .reduce((acc, posicions) => [...acc, ...posicions], [])
);

const getImportedPestanyes = (posicionsLogPerPestanya) => (
    Object.values(posicionsLogPerPestanya || {})
        .filter(entry => entry)
        .map(entry => Object.entries(entry))
        .flat()
        .filter(([_, data]) => data?.posicions?.length > 0)
        .map(([pestanyaName]) => normalizePestanya(pestanyaName))
);

const getImportedCaixes = (json = {}, posicionsLogPerPestanya = {}) => {
    const importedPestanyes = getImportedPestanyes(posicionsLogPerPestanya);

    if (Object.keys(json || {}).length === 0 || importedPestanyes.length === 0) return null;

    return importedPestanyes
        .map(pestanyaName => getIdsInPestanya(json, pestanyaName))
        .flat();
};

const mergeImportedPosicionsWithSources = ({
    json = {},
    originalPosicionsLog = [],
    posicionsLogPerPestanya = {},
    selectedEvent
}) => {
    const mergedImportedPosicions = getMergedImportedPosicions(posicionsLogPerPestanya);
    const importedCaixes = getImportedCaixes(json, posicionsLogPerPestanya);
    const originalPosicions = rebuildPosicions({ posicionsLog: originalPosicionsLog });
    const occupiedOriginalCaixes = Object.keys(originalPosicions.caixes || {})
        .filter(caixaId => caixaMatchesScopedCaixes(importedCaixes, caixaId, json));

    const importedPosicionsToApply = mergedImportedPosicions
        .filter(({ log }) => isFustesLog(log) || !someCaixaMatches(occupiedOriginalCaixes, getCaixaId(log)));

    const caixesToReplace = importedPosicionsToApply
        .filter(({ log }) => !isFustesLog(log))
        .map(({ log }) => getCaixaId(log));

    const filteredOriginalPosicionsLog = originalPosicionsLog
        .filter(log => !caixaMatchesScopedCaixes(caixesToReplace, getCaixaId(log), json));

    return {
        logs: [...importedPosicionsToApply.map(item => item.log), ...filteredOriginalPosicionsLog],
        sourceEvents: [...importedPosicionsToApply.map(item => item.eventId), ...filteredOriginalPosicionsLog.map(() => selectedEvent)],
    };
};

const mergeImportedPosicionsLog = options => mergeImportedPosicionsWithSources(options).logs;

export {
    mergeImportedPosicionsLog,
    mergeImportedPosicionsWithSources
};
